import { useState, useEffect } from 'react';
import { Navigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { CheckademicLogo } from '@/components/CheckademicLogo';
import { SchoolLogo } from '@/components/SchoolLogo';
import { LanguageToggle } from '@/components/parent/LanguageToggle';
import { usePageTitle } from '@/hooks/usePageTitle';
import { isProduction } from '@/utils/environment';
import { buttonStyles } from '@/styles/buttons';
import { authAlertStyles } from '@/styles/authPageStyles';

const MESSAGE_KEY_MAP: Record<string, string> = {
  password_reset: 'auth.login.message.passwordReset',
  password_set: 'auth.login.message.passwordSet',
  registration_complete: 'auth.login.message.registrationComplete',
};

type MessageKey = keyof typeof MESSAGE_KEY_MAP;

const LoginPage = () => {
  const { t } = useTranslation('common');
  usePageTitle(t('auth.login.pageTitle'));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [subdomain, setSubdomain] = useState<string | null>(null);
  const [successMessageKey, setSuccessMessageKey] = useState<MessageKey | null>(null);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { login, isLoading, isAuthenticated, error, tenant, user } = useAuthStore();
  const rawLinkCode = searchParams.get('linkCode');
  const linkCode = rawLinkCode ? rawLinkCode.toUpperCase().replace(/[^A-F0-9]/g, '').slice(0, 6) : null;
  const redirectTo = searchParams.get('redirect');

  // Show success/info messages from query params (e.g. after password reset or session expiry)
  useEffect(() => {
    const message = searchParams.get('message');
    const sessionExpired = searchParams.get('session_expired');
    let changed = false;

    if (message && message in MESSAGE_KEY_MAP) {
      setSuccessMessageKey(message as MessageKey);
      searchParams.delete('message');
      changed = true;
    }
    if (sessionExpired) {
      setShowSessionExpired(true);
      searchParams.delete('session_expired');
      changed = true;
    }
    if (changed) {
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Extract subdomain from URL
  useEffect(() => {
    const hostname = window.location.hostname;
    // Extract subdomain (e.g., "demo" from "demo.localhost" or "demo.checkademic.com")
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[0] !== 'www' && parts[0] !== 'admin') {
      setSubdomain(parts[0]);
    }
  }, []);

  if (isAuthenticated) {
    if (linkCode && user?.role === 'parent') {
      return <Navigate to={`/parent/register?code=${linkCode}`} replace />;
    }
    if (redirectTo && redirectTo.startsWith('/') && !redirectTo.startsWith('//')) {
      return <Navigate to={redirectTo} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await login(email, password);
    } catch {
      // Error is handled by the store
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'var(--surface-color)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--background-color)',
    padding: '2rem',
    borderRadius: 'var(--radius)',
    boxShadow: 'var(--shadow-lg)',
    width: '100%',
    maxWidth: '400px',
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '2rem',
  };

  const subtitleStyle: React.CSSProperties = {
    color: 'var(--text-secondary)',
    fontSize: '0.875rem',
  };

  const formStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  };

  const errorStyle: React.CSSProperties = {
    backgroundColor: 'var(--alert-error-bg)',
    color: 'var(--alert-error-text)',
    padding: '0.75rem',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--alert-error-border)',
    fontSize: '0.875rem',
    margin: '-1rem 0 1rem 0',
  };

  const successStyle: React.CSSProperties = {
    backgroundColor: 'var(--alert-success-bg)',
    color: 'var(--alert-success-text)',
    padding: '0.75rem',
    borderRadius: 'var(--radius)',
    border: '1px solid var(--alert-success-border)',
    fontSize: '0.875rem',
    marginBottom: '1rem',
  };


  const demoInfoStyle: React.CSSProperties = {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: 'var(--surface-color)',
    borderRadius: 'var(--radius)',
    fontSize: '0.775rem',
    color: 'var(--text-secondary)',
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <LanguageToggle />
        <div style={headerStyle}>
          {/* School Logo (if uploaded) */}
          {tenant?.settings.branding.logoFileId && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
              <SchoolLogo logoFileId={tenant.settings.branding.logoFileId} schoolName={tenant.schoolName} size="md" />
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
            <CheckademicLogo
              primaryColor="var(--primary-color)"
              accentColor="var(--secondary-color)"
              textColor="var(--primary-color)"
            />
          </div>
          {tenant ? (
            <p style={subtitleStyle}>{t('auth.login.signInTo', { schoolName: tenant.schoolName })}</p>
          ) : (
            <p style={subtitleStyle}>{t('auth.login.subtitle')}</p>
          )}
        </div>

        {linkCode && (
          <div
            style={{
              backgroundColor: 'var(--alert-info-bg, var(--bg-secondary))',
              color: 'var(--alert-info-text, var(--text-primary))',
              padding: '0.75rem',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--alert-info-border, var(--border-color))',
              fontSize: '0.875rem',
              marginBottom: '1rem',
            }}
          >
            {t('auth.login.linkCodeInfo')}
          </div>
        )}
        {successMessageKey && <div style={successStyle}>{t(MESSAGE_KEY_MAP[successMessageKey])}</div>}
        {showSessionExpired && <div style={authAlertStyles.info}>{t('auth.login.sessionExpired')}</div>}
        {error && <div style={errorStyle}>{error}</div>}

        <form onSubmit={handleSubmit} style={formStyle}>
          <div>
            <label htmlFor="email" className="form-label">
              {t('form.emailAddress')}
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="form-input"
              required
              disabled={isLoading}
              autoComplete="username"
            />
          </div>
          <div>
            <label htmlFor="password" className="form-label">
              {t('form.password')}
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-input"
              required
              disabled={isLoading}
              minLength={8}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            style={{
              ...buttonStyles.primary,
              padding: '0.75rem 1rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1,
            }}
            disabled={isLoading}
          >
            {isLoading ? t('auth.login.signingIn') : t('auth.login.signIn')}
          </button>

          <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
            <Link
              to="/auth/request-password-reset"
              style={{
                color: 'var(--primary-color)',
                fontSize: '0.875rem',
                textDecoration: 'none',
              }}
            >
              {t('auth.login.forgotPassword')}
            </Link>
          </div>

          {tenant?.settings?.contactEmail && (
            <div style={{ textAlign: 'center' }}>
              <a
                href={`mailto:${tenant.settings.contactEmail}?subject=${encodeURIComponent(`Login Help - ${tenant.schoolName}`)}`}
                style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.875rem',
                  textDecoration: 'none',
                }}
              >
                {t('auth.login.needHelp')}
              </a>
            </div>
          )}
        </form>

        {!isProduction() && (
          <div style={demoInfoStyle}>
            <p style={{ fontSize: '0.85rem', marginBottom: '5px' }}>
              <strong>Test Accounts:</strong>
            </p>
            {subdomain ? (
              <>
                <p>Admin: admin@{subdomain}.com</p>
                <p>Teachers: teacher1@{subdomain}.com, teacher2@..., etc.</p>
                <p>Support Staff: staff@{subdomain}.com</p>
                <p>Parents: parent1@{subdomain}.com, parent2@..., etc.</p>
                <p style={{ fontSize: '0.65rem', marginTop: '0.5rem', fontStyle: 'italic' }}>
                  Contact a Checkademic admin for the password.
                </p>
              </>
            ) : (
              <p>Loading credentials...</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
